import * as React from 'react';

import { useNavigate } from 'js/lib/useRouter';

import type { InviteBatchFailureReason, InviteBatchInputMethod } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { useEnterpriseAdminTrackingData } from 'bundles/enterprise-admin-hooks/hooks/useEnterpriseAdminTrackingData';
import { useSystemProgram } from 'bundles/enterprise-admin-hooks/hooks/useSystemProgram';
import InviteErrorDialog from 'bundles/enterprise-admin-learners/components/invite-learners/InviteErrorDialog';
import InviteSuccessDialog from 'bundles/enterprise-admin-learners/components/invite-learners/InviteSuccessDialog';
import NoInvitesSentDialog from 'bundles/enterprise-admin-learners/components/invite-learners/NoInvitesSentDialog';
import PartialInviteSuccessDialog from 'bundles/enterprise-admin-learners/components/invite-learners/PartialInviteSuccessDialog';
import type { FailedInvite, InviteLearnersResult } from 'bundles/enterprise-admin-learners/hooks/useInviteLearners';
import { useManageableGroups } from 'bundles/enterprise-admin-learners/hooks/useManageableGroups';
import { LEARNERS_MODAL } from 'bundles/enterprise-admin-learners/utils/eventing';
import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';

import _t from 'i18n!nls/enterprise-admin-learners';

export type InviteOutcomeSummaryProps = {
  open: boolean;
  /** Result of an invite submission — the literal `'error'` covers a total-failure/thrown case. */
  outcome: InviteLearnersResult | 'error';
  onClose: () => void;
  /** Which method produced this outcome — carried on the `enterprise_admin_invite_result` event. */
  inputMethod: InviteBatchInputMethod;
  /** CSV-only: passed through to NoInvitesSentDialog's "Re-upload CSV" action. */
  onReupload?: () => void;
};

/**
 * Best-effort `inviteBatch.failureReason` derived from the CSV adapter's per-row `errorCode`s
 * (see `adaptCsvSyncResponse.ts`) — email invites don't populate `failed` with these codes today,
 * so this naturally resolves to `undefined` for that path rather than a guessed value.
 */
const getBatchFailureReason = (failed: FailedInvite[]): InviteBatchFailureReason | undefined => {
  const codes = new Set(failed.map((f) => f.errorCode));
  const groupCodes = ['GROUP_NAME_INVALID', 'GROUP_AMBIGUOUS', 'GROUP_NOT_MANAGEABLE', 'GROUP_ABSENT_FOR_ROW'];
  if (groupCodes.some((code) => codes.has(code))) return 'no_group';
  if (codes.has('INVALID_EMAIL') || codes.has('INVALID_NAME') || codes.has('INVALID_EXTERNAL_ID')) {
    return 'invalid_or_missing_data';
  }
  return undefined;
};

/**
 * Success/partial-success/error summary shown after an email invite submission
 * (ENTADMIN-1957). Accepts `InviteLearnersResult | 'error'` directly, the same union
 * `EmailInviteModal` already produces — no CSV-specific branching, since ENTADMIN-2082's
 * CSV submission will have its own, differently-shaped result to adapt into this component.
 *
 * A thin dispatcher: resolves the shared group/navigation context once, then picks which
 * of the four outcome dialogs to render. `InviteSuccessDialog` and `PartialInviteSuccessDialog`
 * match design mockups; `InviteErrorDialog` and `NoInvitesSentDialog` have no mockup yet and
 * keep their earlier, simpler treatment.
 */
const InviteOutcomeSummary = ({ open, outcome, onClose, inputMethod, onReupload }: InviteOutcomeSummaryProps) => {
  const org = useThirdPartyOrganization();
  const navigate = useNavigate();
  const { manageableGroups } = useManageableGroups();
  const { systemProgram } = useSystemProgram(org.id);
  const track = useTracker();
  const { enterprise } = useEnterpriseAdminTrackingData();
  const { organizationSlug, programSlug } = enterprise;

  const failed = outcome === 'error' ? [] : outcome.failed;
  const invitedCount = outcome === 'error' ? 0 : outcome.successful.length + outcome.membershipEmails.length;

  React.useEffect(() => {
    // The thrown-exception case renders InviteErrorDialog instead (a generic failure, not a
    // real outcome), so it's out of this event's scope — tracking it here would emit
    // `invitedCount: 0, failedCount: 0` with no `failureReason`, indistinguishable from a
    // genuine empty-batch result.
    if (!open || outcome === 'error') return;
    // Derived from `outcome` directly (rather than the outer `failed`/`invitedCount`) so this
    // effect's only real dependency for the impression's content is the stable `outcome` prop,
    // not a fresh `[]`/count recomputed every render.
    const outcomeFailed = outcome.failed;
    const outcomeInvitedCount = outcome.successful.length + outcome.membershipEmails.length;
    track('view_modal', {
      modal: { name: LEARNERS_MODAL.INVITE_RESULT },
      inviteBatch: {
        inputMethod,
        invitedCount: outcomeInvitedCount,
        failedCount: outcomeFailed.length,
        failureReason: getBatchFailureReason(outcomeFailed),
        isAsync: false,
      },
      enterprise: { organizationSlug, programSlug },
    });
  }, [open, outcome, inputMethod, track, organizationSlug, programSlug]);

  if (outcome === 'error') {
    return <InviteErrorDialog open={open} onClose={onClose} />;
  }

  const { programId } = outcome;
  const hasInvited = invitedCount > 0;
  const hasFailures = failed.length > 0;
  // The system program can also appear in `manageableGroups` — always treat a match
  // against it as the org-level (no group known) invite flow, not a real group.
  const isSystemProgram = Boolean(systemProgram) && programId === systemProgram?.id;
  const group = manageableGroups.find((manageableGroup) => manageableGroup.id === programId);

  if (!hasInvited) {
    return <NoInvitesSentDialog open={open} onClose={onClose} failed={failed} onReupload={onReupload} />;
  }

  const groupName = isSystemProgram ? undefined : group?.name;
  const canViewGroup = !isSystemProgram && Boolean(group?.slug);
  const onViewGroup = () => {
    // The invited learners just landed with status INVITED — default the group's learner
    // list to that filter instead of its usual "Joined" default so they're visible. A hard
    // redirect (not the SPA router) so the destination page re-mounts and actually picks up
    // that URL param, rather than reusing whatever learner-list state was already mounted.
    if (canViewGroup && group?.slug) window.location.assign(`/o/${org.slug}/admin/groups/${group.slug}?status=INVITED`);
    onClose();
  };
  const onViewAllLearners = () => {
    // Same as above, for the org-wide learners list.
    window.location.assign(`/o/${org.slug}/admin/learners?status=INVITED`);
    onClose();
  };
  const onCurateCatalog = () => {
    navigate(`/o/${org.slug}/admin/catalog/curated-catalog`);
    onClose();
  };
  const onAssignContent = () => {
    navigate(`/o/${org.slug}/admin/catalog/assigned-content`);
    onClose();
  };

  if (!hasFailures) {
    return (
      <InviteSuccessDialog
        open={open}
        onClose={onClose}
        invitedCount={invitedCount}
        groupName={groupName}
        canViewGroup={canViewGroup}
        onViewGroup={onViewGroup}
        onViewAllLearners={onViewAllLearners}
        onAssignContent={onAssignContent}
        onCurateCatalog={onCurateCatalog}
      />
    );
  }

  return (
    <PartialInviteSuccessDialog
      open={open}
      onClose={onClose}
      invitedCount={invitedCount}
      failed={failed}
      groupName={groupName ?? _t('the group')}
      canViewGroup={canViewGroup}
      onViewGroup={onViewGroup}
    />
  );
};

export default InviteOutcomeSummary;
